import * as v from 'valibot';

/**
 * Per-environment config the container injects at startup. The image is built
 * ONCE (env-neutral) and promoted by re-tag; the environment's Medplum endpoint,
 * OAuth client id, PostHog keys, etc. are written into `window.__APP_CONFIG__` by
 * docker-entrypoint.sh from Cloud Run env vars, using un-prefixed keys (the same
 * names Cloud Run already sets, e.g. MEDPLUM_BASE_URL). In local dev and tests
 * this global is absent, so we fall back to Vite's `import.meta.env` (VITE_*).
 *
 * These keys map onto the VITE_* schema keys below so the rest of the app keeps
 * reading `env.VITE_MEDPLUM_BASE_URL` etc. unchanged.
 */
export type AppRuntimeConfig = Partial<{
  MEDPLUM_BASE_URL: string;
  MEDPLUM_CLIENT_ID: string;
  APP_ENV: string;
  LOG_LEVEL: string;
  POSTHOG_KEY: string;
  POSTHOG_HOST: string;
}>;

declare global {
  // Augmenting the built-in Window requires interface merging, not a type alias.
  /* eslint-disable-next-line no-restricted-syntax, @typescript-eslint/consistent-type-definitions */
  interface Window {
    __APP_CONFIG__?: AppRuntimeConfig;
  }
}

const RUNTIME_TO_VITE_KEY: Record<keyof AppRuntimeConfig, string> = {
  MEDPLUM_BASE_URL: 'VITE_MEDPLUM_BASE_URL',
  MEDPLUM_CLIENT_ID: 'VITE_MEDPLUM_CLIENT_ID',
  APP_ENV: 'VITE_APP_ENV',
  LOG_LEVEL: 'VITE_LOG_LEVEL',
  POSTHOG_KEY: 'VITE_PUBLIC_POSTHOG_KEY',
  POSTHOG_HOST: 'VITE_PUBLIC_POSTHOG_HOST',
};

// Runtime environment validation schema
const envSchema = v.object({
  // Medplum configuration
  VITE_MEDPLUM_BASE_URL: v.pipe(
    v.string('VITE_MEDPLUM_BASE_URL is required'),
    v.url('VITE_MEDPLUM_BASE_URL must be a valid URL'),
  ),
  VITE_MEDPLUM_CLIENT_ID: v.pipe(
    v.string('VITE_MEDPLUM_CLIENT_ID is required'),
    v.minLength(1, 'VITE_MEDPLUM_CLIENT_ID cannot be empty'),
  ),
  // Development configuration
  VITE_APP_ENV: v.optional(
    v.union([
      v.literal('development'),
      v.literal('dev'),
      v.literal('qa'),
      v.literal('staging'),
      v.literal('preprod'),
      v.literal('production'),
    ]),
    'development',
  ),
  VITE_LOG_LEVEL: v.optional(
    v.union([
      v.literal('debug'),
      v.literal('info'),
      v.literal('warn'),
      v.literal('error'),
      v.literal('none'),
    ]),
    'info',
  ),
  // PostHog error tracking (optional — omit in local/test builds)
  VITE_PUBLIC_POSTHOG_KEY: v.optional(v.string()),
  VITE_PUBLIC_POSTHOG_HOST: v.optional(v.pipe(v.string(), v.url()), 'https://us.i.posthog.com'),
});

// Values injected by the container at startup take precedence over anything Vite
// baked in; absent (local dev / tests) leaves the VITE_* fallbacks in place.
function runtimeOverrides(): Record<string, unknown> {
  const config = typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined;
  if (!config) return {};
  const out: Record<string, unknown> = {};
  for (const [runtimeKey, viteKey] of Object.entries(RUNTIME_TO_VITE_KEY)) {
    const value = config[runtimeKey as keyof AppRuntimeConfig];
    if (value !== undefined && value !== '') {
      out[viteKey] = value;
    }
  }
  return out;
}

// Validate and export environment variables
function validateRuntimeEnv() {
  // Vite injects empty strings for unset build args (e.g. `--build-arg FOO=`).
  // Treat those as missing so `v.optional(..., default)` falls back to the default.
  const raw = Object.fromEntries(
    Object.entries(import.meta.env as Record<string, unknown>).map(([k, val]) => [
      k,
      val === '' ? undefined : val,
    ]),
  );
  Object.assign(raw, runtimeOverrides());
  try {
    return v.parse(envSchema, raw);
  } catch (error) {
    if (error instanceof v.ValiError) {
      // eslint-disable-next-line no-restricted-syntax
      console.error('Environment validation failed at runtime:');
      for (const issue of error.issues) {
        // @ts-expect-error unneeded check on issue path
        // eslint-disable-next-line no-restricted-syntax
        console.error(`${issue.path?.map((p) => p.key).join('.') || 'root'}: ${issue.message}`);
      }
      throw new Error('Invalid environment configuration');
    }
    throw error;
  }
}

// Validated environment variables
export const env = validateRuntimeEnv();

// Type export for use in other files
export type Env = typeof env;
